/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  SyntheticEvent,
  useCallback
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {EntityStatus, GenericObject} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {FormCheckBox} from '@ion/dls/src/components/forms/FormCheckBox';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import EditorDialog from '@ion/dls/src/editors/EditorDialog';
import styles from '@ion/dls/src/editors/editorStyles';
import WrappedLoadingField from '@ion/dls/src/editors/WrappedLoadingField';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {triggerExpressions} from './ViewDeviceEditorUtils';

export interface ViewAlertConfigEditorProps {
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  alertConfigData: GenericObject;
  loading?: boolean;
  open: boolean;
}

/**
 * The Alert Configuration editor with set fields and encapsulated logic
 * for handling the form's state, including validation.
 *
 * @param {object} alertConfigData: alertConfigData data to be loaded into the editor
 * @param {Function} onCloseDialog: Callback function fired when the dialog requests to be closed
 * @param {boolean} loading: disables any for submission while the form is busy
 */
export const ViewAlertConfigEditor = ({
  open,
  alertConfigData,
  onCloseDialog,
  loading = false
}: ViewAlertConfigEditorProps) => {
  const editorStyles = useExtendStyles(styles);
  const classes      = editorStyles();

  const getAlertAction = useCallback(() => {
    const alertActionArray = [];
    alertConfigData?.alertActionLinks.map((item) => {
      if (item.status !== EntityStatus.Deleted) {
        alertActionArray.push(item.rel);
      }
    });
    alertActionArray.sort();
    return alertActionArray?.join('/') || '';
  }, [alertConfigData]);

  const getTriggerExpression = useCallback(() => {
    const alertTriggerDetails = [];
    if (alertConfigData) {
      const {triggerValues, placeHolders} = alertConfigData;
      placeHolders && Object.entries(placeHolders)?.forEach(function([name, value]) {
        const trigExpression = triggerValues[name];
        const placeHolder = placeHolders[name];
        const trigExpressionArr  = trigExpression.split(' ');
        const placeOperator = triggerExpressions[trigExpressionArr[1]];
        const [placeKey, placeValue] = Object.entries(placeHolder)[0];
        const triggerStr = (placeKey + ' ' + placeOperator + ' ' + placeValue).trim();
        if (triggerStr !== '') {
          alertTriggerDetails.push(triggerStr);
        }
      });
    }
    return alertTriggerDetails?.join(', ') || '';
  }, [alertConfigData]);

  return (
    <EditorDialog open={open}
      dialogTitle={translate('admin-devices:view.alertHistory.alertConfig.title')}
      onCloseDialog={onCloseDialog}>
      <div className={classes.formWrapper}>
        <FormSection title={translate('admin-devices:view.alertHistory.alertConfig.generalInfo')}>
          <>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.name')}
                  value={alertConfigData?.name || ''}/>
              </WrappedLoadingField>
            </FormItem>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.alertType')}
                  value={alertConfigData?.alertType?.title || ''}/>
              </WrappedLoadingField>
            </FormItem>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.description')}
                  value={alertConfigData?.description || ''}/>
              </WrappedLoadingField>
            </FormItem>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.status')}
                  value={alertConfigData?.status || ''}/>
              </WrappedLoadingField>
            </FormItem>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.snoozeDuration')}
                  value={alertConfigData?.snoozeDuration}/>
              </WrappedLoadingField>
            </FormItem>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormCheckBox disabled
                  label={translate('admin-devices:view.alertHistory.alertConfig.processSubAccounts')}
                  defaultChecked={alertConfigData?.processForSubAccounts} />
              </WrappedLoadingField>
            </FormItem>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.triggerExpression')}
                  value={getTriggerExpression()}/>
              </WrappedLoadingField>
            </FormItem>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.alertAction')}
                  value={getAlertAction()}/>
              </WrappedLoadingField>
            </FormItem>
          </>
        </FormSection>
        <FormSection title={translate('admin-devices:view.alertHistory.alertConfig.alertInfo')}>
          <>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.createdOn')}
                  value={formatDateTime(alertConfigData?.createdOn)}/>
              </WrappedLoadingField>
            </FormItem>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.activatedOn')}
                  value={formatDateTime(alertConfigData?.activatedOn)}/>
              </WrappedLoadingField>
            </FormItem>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.lastmodifiedOn')}
                  value={formatDateTime(alertConfigData?.lastModifiedOn)}/>
              </WrappedLoadingField>
            </FormItem>
            <FormItem>
              <WrappedLoadingField loading={loading}>
                <FormTextField disabled
                  fieldLabel={translate('admin-devices:view.alertHistory.alertConfig.deletedOn')}
                  value={formatDateTime(alertConfigData?.deletedOn)}/>
              </WrappedLoadingField>
            </FormItem>
          </>
        </FormSection>
      </div>
    </EditorDialog>
  );
};

export default ViewAlertConfigEditor;
